import { InternalId, StaticReference, StaticRevisionReference } from '@edgebox/data-definition-kit';
import {
  ContentOptimizationStatus,
  ContentOptimizationTargetStage,
  ContentOptimizationTypeStatus,
  ContentRepresentation,
  ISyncCoreContentItem,
} from '@edgebox/sync-core-data-definitions';
import { ClientContentOptimizationDraft, ClientContentOptimizationTypeEntity, ISyncCoreApi } from '@edgebox/sync-core-rest-client';

/**
 * How an issue a health check found on a page is optimized: which optimization types address its issue
 * type, and the one create that starts an optimization of the page with them. The page's Health tab and
 * the issue list both start it through here, so an issue is optimized the same way wherever it is started.
 */

/** Every active optimization type of a project, read at once. */
const OPTIMIZATION_TYPE_PAGE_SIZE = 200;

/** The optimization the create started, and the task that writes its draft. */
export interface IStartedOptimization {
  optimizationId: InternalId;
  taskId?: InternalId;
}

/** The optimization types that are active, which are the ones an optimization can be started with. */
export async function loadActiveOptimizationTypes(api: ISyncCoreApi): Promise<ClientContentOptimizationTypeEntity[]> {
  const listed = await api.governance.contentOptimizationTypes.list(
    { page: 0, itemsPerPage: OPTIMIZATION_TYPE_PAGE_SIZE },
    { statuses: [ContentOptimizationTypeStatus.Active] }
  );
  return listed.items || [];
}

/** The keys of the optimization types that address each issue type, under the issue type's key. */
export function optimizationTypeKeysByIssueTypeKey(types: ClientContentOptimizationTypeEntity[]): Record<string, string[]> {
  const map: Record<string, string[]> = {};

  for (const type of types) {
    const typeKey = String(type.key || '').trim();
    if (!typeKey) {
      continue;
    }

    for (const issueType of type.targetIssueTypes || []) {
      const issueTypeKey = issueType.getStableKey();
      if (!issueTypeKey) {
        continue;
      }

      if (!map[issueTypeKey]) {
        map[issueTypeKey] = [];
      }

      if (!map[issueTypeKey].includes(typeKey)) {
        map[issueTypeKey].push(typeKey);
      }
    }
  }

  return map;
}

/** The optimization types named by their keys, in the order the keys name them; a key no type carries is left out. */
export function optimizationTypesByKeys({
  types,
  typeKeys,
}: {
  types: ClientContentOptimizationTypeEntity[];
  typeKeys: string[];
}): ClientContentOptimizationTypeEntity[] {
  const typesByKey = new Map(
    types
      .map((type) => {
        const typeKey = String(type.key || '').trim();
        return typeKey ? [typeKey, type] : undefined;
      })
      .filter((entry): entry is [string, ClientContentOptimizationTypeEntity] => !!entry)
  );

  return typeKeys.map((typeKey) => typesByKey.get(typeKey)).filter((type): type is ClientContentOptimizationTypeEntity => !!type);
}

/**
 * Starts one optimization of a page with the given types, for the site the page belongs to. The create
 * answers with the optimization it started and the task that writes its draft, which is what a screen
 * follows from here; an answer naming no optimization gives nothing, and the screen says so in its words.
 */
export async function createCheckOptimization(
  api: ISyncCoreApi,
  { page, types }: { page: ISyncCoreContentItem; types: ClientContentOptimizationTypeEntity[] }
): Promise<IStartedOptimization | undefined> {
  const site = page.site ? await page.site.get().catch(() => undefined) : undefined;
  if (!site) {
    throw new Error('Unable to resolve site reference for this content item.');
  }

  const draft = new ClientContentOptimizationDraft();
  draft.targetStage = ContentOptimizationTargetStage.Published;
  draft.status = ContentOptimizationStatus.Generating;
  draft.site = new StaticReference(site);
  draft.types = types.map((type) => new StaticRevisionReference(type));
  draft.fixesIssues = [];
  draft.fixesIssueTypes = [];

  if (page.type === ContentRepresentation.Page) {
    draft.inputPage = new StaticRevisionReference(page);
  } else {
    draft.inputEntity = new StaticRevisionReference(page);
  }

  const response = await api.governance.contentOptimizations.create(draft);
  const started = response.optimizations?.[0];
  const startedId = started?.contentOptimization?.id;
  if (!startedId) {
    return undefined;
  }

  return { optimizationId: startedId as InternalId, taskId: started.task?.id as InternalId | undefined };
}
